import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  Injector,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  booleanAttribute,
  inject,
} from '@angular/core';

import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
  ValidatorFn,
} from '@angular/forms';
import clsx from 'clsx';
import { IconInfoComponent } from '@pedix-workspace/angular-ui-icons';
import {
  WithDefaultValidatorProps,
  composeValidatorFn,
  hasChangedAnyDefaultValidatorProps,
} from '../utils/defaultValidator';
import { UiFormReactiveConfigService } from '../form-reactive-config.service';

let INPUT_UID = 0;

export type FormValidationValueType = string | number | boolean | undefined | null;

@Component({
  selector: 'pxw-form-validation',
  templateUrl: './form-validation.component.html',
  styleUrl: './form-validation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: FormValidationComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: FormValidationComponent,
    },
  ],
  imports: [IconInfoComponent],
})
export class FormValidationComponent
  implements OnInit, OnChanges, ControlValueAccessor, Validator, WithDefaultValidatorProps
{
  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  @Input({ required: true }) name: string;
  @Input() messages: { [key: string]: string } = {};
  @Input() displayErrors = false;

  @Input({ transform: booleanAttribute }) required?: boolean;
  @Input({ transform: booleanAttribute }) requiredTrue?: boolean;
  @Input({ transform: booleanAttribute }) email?: boolean;
  @Input() minLength?: number;
  @Input() maxLength?: number;
  @Input() pattern?: RegExp | readonly RegExp[];
  @Input() min?: number;
  @Input() max?: number;
  @Input() custom?: ValidatorFn;

  private injector = inject(Injector);

  protected value: FormValidationValueType;
  protected inputId: string;
  protected isDisabled: boolean;

  private onChange: (value: FormValidationValueType) => void;
  private onTouched: () => void;
  private onValidatorChange: () => void;
  private validatorFn: ValidatorFn | null;
  private formReactiveConfigService = inject(UiFormReactiveConfigService);

  @HostBinding('class') get classNames() {
    return clsx(`ui-form-validation`, {
      'ui-input--with-errors': !this.isValid,
    });
  }

  get formControl() {
    const ngControl = this.injector.get(NgControl, null);

    if (ngControl) {
      return ngControl.control as FormControl;
    }
    return null;
  }

  get isValid() {
    return this.formControl?.valid;
  }

  get isTouched() {
    return this.formControl?.touched;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get errorEntries(): [string, any][] {
    return Object.entries(this.formControl?.errors || {});
  }

  ngOnInit(): void {
    this.inputId = `form-validation-${++INPUT_UID}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (hasChangedAnyDefaultValidatorProps(changes)) {
      this.validatorFn = composeValidatorFn(this);

      if (this.formControl) {
        this.formControl.updateValueAndValidity();
      }
    }
  }

  writeValue(value: string) {
    this.value = value;
  }
  registerOnChange(onChange: (value: FormValidationValueType) => void) {
    this.onChange = onChange;
  }
  registerOnTouched(onTouched: () => void) {
    this.onTouched = onTouched;
  }
  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  validate(control: AbstractControl): ValidationErrors | null {
    return this.validatorFn?.(control) || null;
  }
  registerOnValidatorChange(onValidatorChange: () => void): void {
    this.onValidatorChange = onValidatorChange;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected getErrorMessage([key, data]: [string, any]) {
    return this.messages?.[key] || this.formReactiveConfigService.getErrorMessage(key, data);
  }
}
